import React, { useEffect, useRef } from 'react';
import { CountBadge } from './CountBadge';

export interface TabBarItem {
  value: string;
  label: string;
  icon?: React.ReactNode;
  /** Optional count badge shown after the label (e.g. unread / item count). */
  count?: number;
}

interface TabBarProps {
  tabs: TabBarItem[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

/**
 * Tab bar — flat row with a bottom-border underline on the active tab.
 * Active tab gets bold foreground text + amber underline; inactive tabs
 * are muted. Optional count badge after each label.
 */
export const TabBar: React.FC<TabBarProps> = ({ tabs, value, onChange, className = '' }) => {
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Keep the active tab visible inside the horizontally-scrolling bar —
  // on narrow viewports the active tab might otherwise sit off-screen
  // (e.g. auto-selected by parent), leaving the user looking at the
  // wrong section's content.
  useEffect(() => {
    const el = tabRefs.current[value];
    if (el) el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [value]);

  return (
    <div
      className={`relative flex gap-0.5 overflow-x-auto scrollbar-custom border-b border-foreground/10 bg-zinc-100 dark:bg-zinc-800/40 px-2 ${className}`}
    >
      {tabs.map((tab) => {
        const isActive = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={(el) => {
              tabRefs.current[tab.value] = el;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.value)}
            className={`relative inline-flex items-center gap-1.5 px-3 py-2.5 text-[10.5px] uppercase tracking-wider whitespace-nowrap transition-colors duration-150 flex-shrink-0 border-b-2
              ${isActive
                ? 'text-amber-700 dark:text-amber-300 font-semibold border-amber-500'
                : 'text-muted-foreground font-medium border-transparent hover:text-foreground'
              }
            `}
          >
            {tab.icon && (
              <span
                className={`flex-shrink-0 transition-colors duration-150 ${
                  isActive ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'
                }`}
              >
                {tab.icon}
              </span>
            )}
            <span className="hidden sm:inline">{tab.label}</span>

            {typeof tab.count === 'number' && (
              <CountBadge count={tab.count} variant={isActive ? 'active' : 'muted'} />
            )}
          </button>
        );
      })}
    </div>
  );
};
